import React, { useEffect, useRef, useState } from "react";
import { Typography, Grid, Box } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import callApi from "../../utils/api";
import { SectionCard } from "../study/course/SectionCard";
import Quiz from "../../pages/quiz";
import { htmlToText, nowISO } from "../../utils";
import { styled } from "@mui/material/styles";
import {
  getAssessmentById,
  getQuestionsByAssessmentId,
} from "../../store/course/courseBaseApi";
import { useStudy } from "../../pages/study/hooks";
import AsyncTextToSpeech from "../study/AsyncTextToSpeech";
import AsyncSpeechEvaluation from "../study/AsyncSpeechEvaluation";
import TakeSpeechEvaluation from "../study/course/TakeSpeechEvaluation";
import { useQuiz } from "../../pages/quiz/hooks";
import speechBgSvg from "../../assets/svgs/speechEvaluationIcons/speechBg.svg";
import { makeStyles } from "tss-react/mui";
import AsyncTextToSpeech2 from "../study/AsyncTextToSpeech2";
import {
  parseContentMarkupToJSON,
  parseKeywordsMarkupToJSON,
} from "../markupEditor";
import arrowSvg from "../../assets/svgs/arrow.svg";
import { ButtonBlue } from "../buttons";
import { useDiagnosticTest } from "../../pages/diagnosticTestV2/hooks";

const MANDATE_PASSING = false;
const CONTENT_SEPARATOR = ";";
const MAX_CONTENT_LINES = 2;

const useStyles = makeStyles()((theme) => ({
  containerBox: {
    margin: theme.spacing(2),
    background: "#fff",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    height: "calc(100vh - 190px)",
  },
  gridContainer: {
    height: "100%",
  },
}));

export const Chapter = ({
  data,
  meta,
  mapping,
  sectionId,
  style,
  onComplete,
  setCompletedSentence,
  setTotalSentence,
  assessmentBegin,
  setAssessmentBegin,
  setAssessmentEnded,
  onConfettiClose,
}) => {
  const { classes } = useStyles();
  const { setReadingScore } = useDiagnosticTest();
  const isHTML = useRef(String(data.contentType).toLowerCase() == "html");
  const [content, setContent] = useState(data?.content || null);
  const [contentArray, setContentArray] = useState([]);
  const contentTextArray = React.useMemo(
    () => contentArray.map((item) => item.text),
    [contentArray],
  );
  const [contentUrl, setContentUrl] = useState(null);
  const [chapter, setChapter] = useState({});
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  // const [assessmentBegin, setAssessmentBegin] = useState(false);
  const [isDiagnostic, setIsDiagnostic] = useState(true);
  const [speechEvaluated, setSpeechEvaluated] = useState(false);
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [systemReadingCompleted, setSystemReading] = React.useState(false);
  const [reading, setReading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { gradingResult } = useQuiz();
  const [importantWords, setImportantWords] = useState([]);
  const { setProgress, profile, loadSynonyms, scores, synonyms } = useStudy();
  const { setSpeechEvaluationScore, updateCurrentCourseDetails } = useQuiz();
  const [keywords, setKeywords] = useState();
  const [assessmentScreen, setAssessmentScreen] = useState(false);

  const speechEval = useRef({ start: null, end: null });
  const audioPlayback = useRef({ start: null, end: null });
  const ref = useRef(null);

  const handleFail = () => {
    setSpeechEvaluated(false);
    setAssessmentStarted(false);
    setCompleted(false);
    setReading(false);
  };

  const onSpeechEvaluationCompletion = async (
    evaluationOutcome,
    s,
    averageScore,
  ) => {
    if (speechEvaluated) return;

    speechEval.current.end = nowISO();

    setSpeechEvaluated(true);
    setAssessmentStarted(true);
    setSpeechEvaluationScore(averageScore);
    setReadingScore(averageScore);
  };

  const onSpeechEvaluationStart = () => {
    speechEval.current.start = nowISO();
  };

  const onAudioPlaybackStart = () => {
    audioPlayback.current.start = nowISO();
  };

  const handleComplete = async () => {
    if (onComplete && typeof onComplete === "function") {
      onComplete();
    }

    setCompleted(true);
    setAssessmentStarted(false);
  };

  const onAudioPlaybackRestart = () => {
    // setReading(false)
  };

  const onAudioPlaybackCompletion = async () => {
    console.log("Completed hearing audio");

    if (audioPlayed) return;

    setSystemReading(true);
    setAudioPlayed(true);
  };

  const onStartSpeechEvaluation = () => {
    setSystemReading(false);
    setReading(true);
  };

  React.useEffect(() => {
    sectionId && setProgress({ section: sectionId });
    sectionId && updateCurrentCourseDetails({ sectionId });
  }, [sectionId]);

  React.useEffect(() => {
    if (data && data.contentURL && data.contentURL != contentUrl) {
      setContentUrl(data.contentURL);
      setCompleted(false);
      setAssessmentStarted(false);
      setSpeechEvaluated(false);
    }

    if (
      data.chapterId &&
      (!chapter.chapterId || chapter.chapterId != data.chapterId)
    ) {
      setChapter(data);
      setProgress({ chapter: data.chapterId });
      setCompleted(false);
      setAssessmentStarted(false);
      setSpeechEvaluated(false);
      updateCurrentCourseDetails({ chapterId: data.chapterId });
    }
  }, [data]);

  React.useEffect(() => {
    if (!contentUrl) return;

    let fetchType = null;
    let api = callApi();

    (async () => {
      if (isHTML.current) {
        fetchType = "text";
      }

      let resp = await api.asyncFetchContent(contentUrl, fetchType);
      let text = htmlToText(resp);
      let splitted = text.split(CONTENT_SEPARATOR).filter((e) => e);

      text = splitted.slice(0, MAX_CONTENT_LINES).join(CONTENT_SEPARATOR);
      // Same {text, image} shape the markup effect below produces. This branch
      // used to emit raw strings, so whichever effect resolved last decided the
      // shape — and the string shape broke every `q.text` read downstream.
      let textArray = splitted
        .slice(0, MAX_CONTENT_LINES)
        .map((sentence) => ({ text: sentence, image: null }));

      setContent(text);
      setContentArray(textArray);
    })();
  }, [contentUrl]);

  const parseContent = (inputText) => {
    // Extracting text within square brackets using regex
    const extractedText = inputText
      .match(/\[(.*?)\]/g) // Matches text inside square brackets
      .map((match) => match.slice(1, -1)) // Removes the brackets
      .join(""); // Concatenates the extracted text

    return extractedText;
  };

  React.useEffect(() => {
    if (data && data.content) {
      const sentences = parseContentMarkupToJSON(data.content);

      // Save sentences and details to respective states
      setContentArray(sentences);
      setContent(sentences);
    }
  }, [data]);

  const parseKeywords = (inputText) => {
    // Extracting text inside square brackets and returning as an array of strings
    const extractedArray = inputText
      .match(/\[(.*?)\]/g) // Matches text inside square brackets
      .map((match) => match.slice(1, -1)); // Removes brackets and returns the text

    return extractedArray;
  };

  React.useEffect(() => {
    if (data && data.keyWords) {
      const parsedImportantWords = parseKeywordsMarkupToJSON(data.keyWords);

      // Update state with the parsed keywords
      setImportantWords(parsedImportantWords);
      setKeywords(parseKeywords(data?.keyWords));
    }
  }, [data]);

  useEffect(() => {
    setTotalSentence(contentArray.length);
    setCompletedSentence(scores.length);
  }, [contentArray, scores]);

  React.useEffect(() => {
    const handleEvent = (e) => {
      if (ref.current && ref.current.contains(e.target)) {
        e.preventDefault();
      }
    };

    document.addEventListener("copy", handleEvent);
    document.addEventListener("paste", handleEvent);

    return () => {
      document.removeEventListener("copy", handleEvent);
      document.removeEventListener("paste", handleEvent);
    };
  }, []);

  return (
    <Box ref={ref} className={classes.containerBox}>
      <Grid container spacing={2} className={classes.gridContainer}>
        {!assessmentBegin && (
          <Grid item xs={12} md={7}>
            <Box>
              <SectionCard completed={audioPlayed}>
                <Box sx={{}}>
                  <AsyncTextToSpeech2
                    contentArray={contentArray}
                    readingComplete={reading}
                    id={chapter.chapterId}
                    contentType={"text"}
                    // importantWords={importantWords}
                    onAudioPlayback={onAudioPlaybackStart}
                    autoPlay={true}
                    onAudioReplay={onAudioPlaybackRestart}
                    onAudioPlaybackComplete={onAudioPlaybackCompletion}
                    syncWithSpeechEvaluation={true}
                    enableTextToAudio={false}
                    speechEvaluated={speechEvaluated}
                  />
                </Box>
              </SectionCard>
              {/* {!speechEvaluated && !completed &&
                                <Box sx={{ display: 'flex', justifyContent: 'center' }} >
                                    <img style={{ width: '80%' }} src={speechBgSvg} />
                                </Box>
                            } */}
              {!isDiagnostic && speechEvaluated && (
                <SectionCard>
                  <h4>Word List</h4>
                  <AsyncTextToSpeech
                    contentArray={importantWords}
                    id={`${data.chapterId}-important-words`}
                    contentType={"text"}
                    autoPlay={true}
                    syncWithSpeechEvaluation={true}
                    enableTextToAudio={true}
                  />
                </SectionCard>
              )}
            </Box>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={!assessmentBegin ? 5 : 12}
          sx={{ alignContent: "center" }}
        >
          {!speechEvaluated && !completed ? (
            <Box
              sx={{
                background: "#fff",
                borderRadius: 1,
                boxShadow: "0px 4px 4px 0px #0000000A",
                height: "100%",
                alignContent: "center",
              }}
            >
              <SectionCard completed={speechEvaluated}>
                <AsyncSpeechEvaluation
                  onComplete={onSpeechEvaluationCompletion}
                  onStart={onSpeechEvaluationStart}
                  sectionId={sectionId}
                  chapterId={data.chapterId}
                  saveHistory={false}
                  skipFailures={true}
                  importantWords={keywords}
                  showText={false}
                  question={contentArray}
                  sampleRate={16000}
                />
              </SectionCard>
            </Box>
          ) : (
            <Box
              sx={{
                background: "#fff",
                borderRadius: 1,
                boxShadow: "0px 4px 4px 0px #0000000A",
                height: "100%",
                position: "relative",
              }}
            >
              {assessmentScreen ? (
                <>
                  {chapter?.assessments && chapter?.assessments.length > 0 && (
                    <SectionCard completed={completed}>
                      <Assessment
                        onComplete={handleComplete}
                        onFail={handleFail}
                        mapping={mapping}
                        assessmentId={chapter.assessments[0]["id"]}
                        setAssessmentBegin={setAssessmentBegin}
                        setAssessmentEnded={setAssessmentEnded}
                        onConfettiClose={onConfettiClose}
                        setAssessmentScreen={setAssessmentScreen}
                      />
                    </SectionCard>
                  )}
                </>
              ) : (
                <Box sx={{}}>
                  {speechEvaluated && (
                    <>
                      <Typography
                        sx={{
                          color: "#447AE2",
                          fontSize: {
                            xs: "1.2em",
                            sm: "1.2em",
                            md: "1.3em",
                            lg: "1.5em",
                            xl: "2em",
                          },
                          fontFamily: '"Quicksand", sans-serif',
                          fontWeight: "700",
                          textAlign: "center",
                        }}
                      >
                        Your Scores
                      </Typography>
                      <Typography
                        sx={{
                          color: "#464255",
                          fontSize: {
                            xs: "0.6em",
                            sm: "0.6em",
                            md: "0.8em",
                            lg: "1em",
                            xl: "1.2em",
                          },
                          fontFamily: '"Quicksand", sans-serif',
                          fontWeight: "700",
                          textAlign: "center",
                          m: 1,
                        }}
                      >
                        Click on ‘Continue’ to Attend the assessment.
                      </Typography>
                    </>
                  )}
                  <SectionCard completed={completed}>
                    <Box
                      sx={{
                        // py: 1,
                        height: "50vh",
                        display: "flex",
                        justifyContent: {
                          xs: "center",
                          sm: "center",
                          md: "flex-start",
                        },
                        overflowY: "scroll",
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#F5F5F5",
                          borderRadius: "10px",
                        },
                        "&:hover, &:focus-within": {
                          "&::-webkit-scrollbar-thumb": {
                            background:
                              "linear-gradient(193.28deg, #29ABE2 5.42%, #2DA2E2 12.18%, #398CE2 33.81%, #417EE2 54.77%, #447AE2 73.02%)",
                            width: "12px",
                            cursor: "pointer",
                          },
                        },
                      }}
                    >
                      <AsyncTextToSpeech
                        contentArray={contentTextArray}
                        readingComplete={reading}
                        id={chapter.chapterId}
                        contentType={"text"}
                        importantWords={importantWords}
                        onAudioPlayback={onAudioPlaybackStart}
                        autoPlay={true}
                        onAudioReplay={onAudioPlaybackRestart}
                        onAudioPlaybackComplete={onAudioPlaybackCompletion}
                        syncWithSpeechEvaluation={true}
                        enableTextToAudio={false}
                        speechEvaluated={speechEvaluated}
                      />
                    </Box>
                  </SectionCard>
                  <ButtonBlue
                    sx={{
                      left: "50%",
                      bottom: "0px",
                      transform: "translateX(-50%)",
                      position: "absolute",
                    }}
                    onClick={() => setAssessmentScreen(true)}
                  >
                    <Typography
                      sx={{
                        fontWeight: "700",
                        fontFamily: '"Quicksand", sans-serif',
                        fontSize: {
                          xs: "1em",
                          sm: "1em",
                          md: "1.4em",
                          lg: "1.6em",
                          xl: "2em",
                        },
                        zIndex: 2,
                      }}
                    >
                      Continue
                    </Typography>
                    <img
                      style={{
                        width: "15%",
                        maxWidth: "100%",
                        marginTop: "5px",
                        height: "auto",
                        zIndex: 2,
                      }}
                      src={arrowSvg}
                      alt=""
                    />
                  </ButtonBlue>
                </Box>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      {!isDiagnostic && (
        <TakeSpeechEvaluation
          open={systemReadingCompleted}
          handleStartEvaluation={onStartSpeechEvaluation}
        />
      )}
    </Box>
  );
};

function Assessment({
  assessmentId,
  onComplete,
  onFail,
  mapping,
  setAssessmentBegin,
  setAssessmentEnded,
  onConfettiClose,
  setAssessmentScreen,
}) {
  const [assessment, setAssessment] = useState(null);
  const { setProgress } = useStudy();

  const { updateCurrentCourseDetails } = useQuiz();

  const handleComplete = () => {
    if (onComplete && typeof onComplete === "function") {
      onComplete();
    }
  };

  React.useEffect(() => {
    if (assessmentId) {
      setProgress({ assessment: assessmentId });
      updateCurrentCourseDetails({ assessmentId });
    }

    (async () => {
      const [assessmentResp, questionsResp] = await Promise.all([
        getAssessmentById({ id: assessmentId }),
        getQuestionsByAssessmentId({ id: assessmentId }),
      ]);

      let _assessment = {
        ...assessmentResp.data,
        questions: questionsResp.questions,
      };

      setAssessment(_assessment);
    })();
  }, [assessmentId]);

  if (!assessment) return null;

  return (
    <Quiz
      data={assessment}
      onFail={onFail}
      mandatePassing={MANDATE_PASSING}
      mandateSpeechEvaluation={true}
      onComplete={handleComplete}
      type={"diagnostic-test-v2"}
      setAssessmentBegin={setAssessmentBegin}
      setAssessmentEnded={setAssessmentEnded}
      onConfettiClose={onConfettiClose}
      setAssessmentScreen={setAssessmentScreen}
    />
  );
}

const styles = {
  container: {
    position: "relative",
    marginTop: "2rem",
  },
  renderer: {
    color: "var(--mui-palette-text-primary)",
  },

  actionBtnContainer: {
    display: "flex",
    justifyContent: "center",
  },
  question: {
    justifyContent: "space-between",
  },
  part: {
    padding: "1rem",
    borderRadius: "10px",
    marginBottom: "1rem",
  },
  image: {
    width: "100%",
  },
};
